import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import MainLayout from "../../../components/Layout/MainLayout";
import FDCSideRail from "../../../components/Common/FDCSideRail";
import { commonService } from "../../../services/commonService";
import { isCXUser } from "../../../utils/permissions";
import { BackIcon } from "../../../components/Dashboard/DataCenterIcons";
import { ROUTES } from "../../../utils/constants";
import "../../../assets/styles/Workflow.css";

// Customer Experience-only, same gating as CustomerExperiencePage.tsx - a
// hardcoded email allowlist on both frontend (isCXUser) and backend
// (CX_USER_EMAILS), not a permission/role. Lets CxEx create a DCCP or
// Spokesly customer login directly, with no FDC project required yet - the
// project-scoped customer-email link (cc_fdc_customer_email) simply can't
// exist until a real project is later created/delivered for this same
// email, at which point the existing delivery flow already finds this
// account by email and picks it up on its own.
const RegisterCustomerPage: React.FC = () => {
  const navigate = useNavigate();
  const [railOpen, setRailOpen] = useState(false);

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [product, setProduct] = useState<"dccp" | "spokesly">("dccp");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  if (!isCXUser()) {
    return (
      <MainLayout>
        <div style={{ padding: 40, textAlign: "center", color: "#6b7280" }}>
          Register Customer isn't available for your account.
        </div>
      </MainLayout>
    );
  }

  const isValid = firstName.trim().length > 0 && email.trim().length > 0 && password.length >= 8;

  const resetForm = () => {
    setFirstName("");
    setLastName("");
    setEmail("");
    setPassword("");
    setProduct("dccp");
  };

  const handleSubmit = async () => {
    if (!isValid) return;
    setSubmitting(true);
    setError(null);
    setSuccess(null);
    try {
      const res: any = await commonService.fdcRegisterCustomer({
        first_name: firstName.trim(),
        last_name: lastName.trim() || undefined,
        email: email.trim(),
        password,
        product,
      });
      setSuccess(`Account created for ${res.email} (${product === "dccp" ? "DCCP" : "Spokesly"}) - active now.`);
      resetForm();
    } catch (e: any) {
      setError((e?.response?.data?.message || e?.response?.message) || "Failed to create the account.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <MainLayout>
      <div style={{ display: "flex", flex: 1, minHeight: 0 }}>
        <FDCSideRail mobileOpen={railOpen} onCloseMobile={() => setRailOpen(false)} />
        <div className="rde-downloads-page" style={{ backgroundColor: "#f5f5f5", flex: 1, overflowY: "auto", padding: "26px 30px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
            <button onClick={() => setRailOpen(true)} className="wf-rail-toggle" aria-label="Open menu">☰</button>
            <button onClick={() => navigate(ROUTES.DASHBOARD)} className="regular-rdc-back-button" style={{ margin: 0 }}>
              <BackIcon />
              Back
            </button>
          </div>

          <div className="wf-section-title" style={{ marginBottom: 4 }}>
            <h3>Register Customer</h3>
          </div>
          <div style={{ color: "#6b7280", fontSize: 13, marginBottom: 18, maxWidth: 560 }}>
            Create a DCCP or Spokesly customer login before any project exists for them. The account is active
            immediately - no invite link, no project required. Once a project is later delivered to this same
            email, it's picked up automatically.
          </div>

          <div className="wf-card wf-card-pad" style={{ maxWidth: 480 }}>
            {error && <div style={{ color: "#ef4444", fontSize: 12.5, marginBottom: 12 }}>{error}</div>}
            {success && <div style={{ color: "#059669", fontSize: 12.5, marginBottom: 12 }}>{success}</div>}

            <div className="wf-form wf-stack" style={{ gap: 14 }}>
              <div className="wf-grid2">
                <div>
                  <div className="wf-field-label">First Name *</div>
                  <input type="text" value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder="First name" />
                </div>
                <div>
                  <div className="wf-field-label">Last Name</div>
                  <input type="text" value={lastName} onChange={(e) => setLastName(e.target.value)} placeholder="Last name (optional)" />
                </div>
              </div>

              <div>
                <div className="wf-field-label">Email *</div>
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="customer@example.com" />
              </div>

              <div>
                <div className="wf-field-label">Password *</div>
                <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Min 8 characters" />
              </div>

              <div>
                <div className="wf-field-label" style={{ marginBottom: 8 }}>Product *</div>
                <div className="wf-radiorow">
                  <label>
                    <input type="radio" checked={product === "dccp"} onChange={() => setProduct("dccp")} /> DCCP
                  </label>
                  <label>
                    <input type="radio" checked={product === "spokesly"} onChange={() => setProduct("spokesly")} /> Spokesly
                  </label>
                </div>
              </div>

              <button
                className="wf-btn wf-btn-primary"
                style={{ alignSelf: "flex-start" }}
                disabled={!isValid || submitting}
                onClick={handleSubmit}
              >
                {submitting ? "Creating…" : "Create Account"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default RegisterCustomerPage;
